import React from 'react';
import Button from '../ui/Button';
import heroLogo from '../../assets/logo.png';

const Hero: React.FC = () => {
  return (
    <div className="relative overflow-hidden bg-neutral-offwhite min-h-[60vh] pb-8">
      {/* Background gentle pattern */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <svg className="text-accent-rose" width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0,0 C40,40 60,40 100,0 V100 H0 Z" fill="currentColor" />
        </svg>
      </div>
      
      {/* Transition ondulée élégante vers le bas */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg className="w-full h-24 md:h-32" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path 
            d="M0,0 C150,80 350,80 600,40 C850,0 1050,0 1200,40 L1200,120 L0,120 Z" 
            fill="white"
            className="drop-shadow-sm"
          />
        </svg>
      </div>
      
      <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16 relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
          <div className="md:w-5/12 md:pr-8 relative z-20 w-full">
            <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl leading-tight text-neutral-ink mb-4">
              <span className="block animate-fade-in-up">
                Douceur et Bien-être
              </span>
              <span className="block text-brand relative inline-block animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <span className="relative">
                  pour la maternité
                  {/* Soulignement animé élégant */}
                  <span className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-accent-rose via-brand to-accent-sand animate-underline-expand"></span>
                </span>
              </span>
            </h1>
            <p className="font-nunito text-neutral-body text-lg mb-8 max-w-md">
              Découvrez notre havre de paix dédié aux futures mamans et aux nouveaux-nés. 
              Un accompagnement personnalisé pour vous chouchouter pendant cette période unique.
            </p>
            
            {/* Image mobile - visible uniquement sur mobile */}
            <div className="md:hidden w-full relative flex items-center justify-center mb-8">
              <div className="relative w-[80%] sm:w-[70%] aspect-square max-w-sm">
                {/* Cercles d'aura animés */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="absolute w-[110%] h-[110%] rounded-full bg-gradient-to-r from-accent-rose/40 to-accent-sand/40 animate-pulse"></div>
                  <div className="absolute w-[90%] h-[90%] rounded-full bg-gradient-to-r from-accent-sand/50 to-accent-rose/50 animate-ping" style={{ animationDuration: '3s' }}></div>
                </div>
                
                {/* Particules flottantes */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-accent-rose/40 rounded-full animate-float" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
                  <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-accent-sand/40 rounded-full animate-float" style={{ animationDelay: '1s', animationDuration: '5s' }}></div>
                  <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-accent-sage/40 rounded-full animate-float" style={{ animationDelay: '2s', animationDuration: '6s' }}></div>
                </div>
                
                {/* Logo */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
                  <div className="absolute inset-0 rounded-full bg-gradient-radial from-accent-rose/25 via-accent-sand/15 to-transparent animate-pulse blur-xl" style={{ animationDuration: '4s' }}></div>
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent animate-shine rounded-full"></div>
                  <img 
                    src={heroLogo} 
                    alt="Logo Doux ce Moman" 
                    className="relative z-10 w-full h-full object-contain animate-heartbeat drop-shadow-2xl"
                    style={{ filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1))' }}
                  />
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 w-full max-w-md">
              {/* Bouton Réserver avec effet liquide */}
              <Button 
                variant="outline" 
                size="md" 
                to="/reservation"
                className="w-full group relative overflow-hidden border-2 border-brand hover:border-brand-dark transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 !bg-transparent hover:!bg-transparent"
              >
                {/* Effet de remplissage liquide */}
                <span className="absolute inset-0 bg-brand opacity-0 translate-y-full group-hover:opacity-100 group-hover:animate-liquid-fill transition-opacity duration-300"></span>
                
                <span className="relative z-10 text-brand group-hover:text-white transition-colors duration-500 flex items-center justify-center gap-2 font-semibold">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Réserver</span>
                  <svg className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Button>
              
              {/* Ligne avec 2 boutons */}
              <div className="flex gap-3">
                {/* Bouton Nos services avec effet liquide */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  to="/services"
                  className="flex-1 group relative overflow-hidden border-2 border-brand hover:border-brand-dark transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 !bg-white/60 backdrop-blur-sm hover:!bg-white/80"
                >
                  {/* Effet de remplissage liquide */}
                  <span className="absolute inset-0 bg-brand opacity-0 translate-y-full group-hover:opacity-100 group-hover:animate-liquid-fill transition-opacity duration-300"></span>
                  
                  <span className="relative z-10 text-brand group-hover:text-white transition-colors duration-500 flex items-center justify-center gap-1.5 font-semibold text-sm">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    <span>Nos services</span>
                    <svg className="w-3.5 h-3.5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Button>
                
                {/* Bouton Bons cadeaux avec effet liquide */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  to="/bons-cadeaux"
                  className="flex-1 group relative overflow-hidden border-2 border-brand hover:border-brand-dark transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 !bg-white/60 backdrop-blur-sm hover:!bg-white/80"
                >
                  {/* Effet de remplissage liquide */}
                  <span className="absolute inset-0 bg-brand opacity-0 translate-y-full group-hover:opacity-100 group-hover:animate-liquid-fill transition-opacity duration-300"></span>
                  
                  <span className="relative z-10 text-brand group-hover:text-white transition-colors duration-500 flex items-center justify-center gap-1.5 font-semibold text-sm">
                    <span className="text-base">🎁</span>
                    <span>Bons cadeaux</span>
                    <svg className="w-3.5 h-3.5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Button>
              </div>
            </div>
          </div>
          
          {/* Image desktop - visible uniquement sur desktop */}
          <div className="hidden md:flex w-full md:w-7/12 relative items-center justify-center">
            {/* Conteneur global réduit de 10% */}
            <div className="relative w-[80%] sm:w-[70%] md:w-full aspect-square max-w-lg">
              {/* Cercles d'aura animés directement autour du logo */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-[110%] h-[110%] rounded-full bg-gradient-to-r from-accent-rose/40 to-accent-sand/40 animate-pulse"></div>
                <div className="absolute w-[90%] h-[90%] rounded-full bg-gradient-to-r from-accent-sand/50 to-accent-rose/50 animate-ping" style={{ animationDuration: '3s' }}></div>
              </div>
              
              {/* Particules flottantes */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-accent-rose/40 rounded-full animate-float" style={{ animationDelay: '0s', animationDuration: '4s' }}></div>
                <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-accent-sand/40 rounded-full animate-float" style={{ animationDelay: '1s', animationDuration: '5s' }}></div>
                <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-accent-sage/40 rounded-full animate-float" style={{ animationDelay: '2s', animationDuration: '6s' }}></div>
                <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-accent-rose/30 rounded-full animate-float" style={{ animationDelay: '1.5s', animationDuration: '4.5s' }}></div>
              </div>
              
              {/* Logo avec tous les effets directement appliqués */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
                {/* Halo doux derrière le logo */}
                <div className="absolute inset-0 rounded-full bg-gradient-radial from-accent-rose/25 via-accent-sand/15 to-transparent animate-pulse blur-xl" style={{ animationDuration: '4s' }}></div>
                
                {/* Effet de brillance tournant */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent animate-shine rounded-full"></div>
                
                {/* Logo avec battement de coeur et ombre douce */}
                <img 
                  src={heroLogo} 
                  alt="Logo Doux ce Moman" 
                  className="relative z-10 w-full h-full object-contain animate-heartbeat drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1))' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;